import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../lib/api'
import type { Client, ClientSite, Proposal, PaginatedResult } from '@shared/types'
import { CardGridSkeleton } from '../components/ui/Skeletons'
import { StatusBadge } from '../components/ui/StatusBadge'
import { GlowCard } from '../components/ui/GlowCard'
import { AddressAutocomplete } from '../components/AddressAutocomplete'
import { getGoogleMapsUrl } from '../lib/mapbox'
import { SiteCreateModal } from '../components/site/SiteCreateModal'
import { Breadcrumb } from '../components/ui/Breadcrumb'
import { EntityHeader } from '../components/ui/EntityHeader'
import { TabBar } from '../components/ui/TabBar'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

type Tab = 'details' | 'proposals' | 'sites' | 'workorders' | 'floorplans' | 'invoices' | 'activity'

const TAB_CONFIG: { id: Tab; label: string; countKey?: string }[] = [
  { id: 'details', label: 'Details' },
  { id: 'proposals', label: 'Proposals', countKey: 'proposals' },
  { id: 'sites', label: 'Sites', countKey: 'sites' },
  { id: 'workorders', label: 'Work Orders', countKey: 'workOrders' },
  { id: 'floorplans', label: 'Floor Plans', countKey: 'floorPlans' },
  { id: 'invoices', label: 'Invoices', countKey: 'invoices' },
  { id: 'activity', label: 'Activity' },
]


const TYPE_LABELS: Record<string, string> = { client: 'Client', employee: 'Employee', contractor: 'Contractor', other: 'Other' }
const TYPE_COLORS: Record<string, string> = { client: 'var(--primary)', employee: 'var(--success)', contractor: 'var(--accent)', other: 'var(--text-tertiary)' }

function fmtMoney(n: number): string {
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function Building2Icon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>
}
function MapPinIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
}
function FileTextIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
}
function DollarIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
}
function EmptyStateIcon({ size = 40, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
}

interface Counts { proposals: number; sites: number; workOrders: number; floorPlans: number; invoices: number }
interface WorkOrder { id: string; slug: string; title: string; type: string; priority: string; status: string; scheduled_date: string | null; assigned_user_name: string | null; created_at: string }
interface Invoice { id: string; slug: string; invoice_number: string; subtotal: number; total: number; status: string; issued_date: string | null; due_date: string | null; created_at: string }
interface FloorPlanItem { id: string; slug: string; name: string; site_name: string; floor_number: number; background_r2_key: string | null }
interface ActivityEntry { id: string; action: string; entity_type: string; entity_id: string; description: string; user_name: string | null; created_at: string }

export default function ClientDetail() {
  const { slug } = useParams<{ slug: string }>()
  const [client, setClient] = useState<(Client & { _counts?: Counts }) | null>(null)
  const [sites, setSites] = useState<ClientSite[]>([])
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [floorPlans, setFloorPlans] = useState<FloorPlanItem[]>([])
  const [activityLog, setActivityLog] = useState<ActivityEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('details')

  // Add Site modal state
  const [showAddSite, setShowAddSite] = useState(false)

  // Edit Client modal state
  const [showEditClient, setShowEditClient] = useState(false)
  const [editForm, setEditForm] = useState({ company_name: '', contact_name: '', email: '', phone: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', notes: '' })
  const [editSaving, setEditSaving] = useState(false)

  const counts = client?._counts || { proposals: 0, sites: 0, workOrders: 0, floorPlans: 0, invoices: 0 }

  const loadData = useCallback(async () => {
    if (!slug) return
    try {
      const c = await api.get<Client & { _counts?: Counts }>(`/clients/${slug}`)
      setClient(c)
      const [sitesRes, proposalsRes] = await Promise.all([
        api.get<{ data: ClientSite[] }>(`/clients/${c.id}/sites?limit=50`).catch(() => ({ data: [] as ClientSite[] })),
        api.get<PaginatedResult<Proposal>>(`/proposals?client_id=${c.id}&limit=50`).catch(() => ({ data: [] as Proposal[] })),
      ])
      setSites(sitesRes.data)
      setProposals(proposalsRes.data)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [slug])

  useEffect(() => { loadData() }, [loadData])

  // Lazy load tab data
  useEffect(() => {
    if (!client) return
    if (tab === 'workorders' && workOrders.length === 0) {
      api.get<{ data: WorkOrder[] }>(`/work-orders?client_id=${client.id}&limit=50`).then(r => setWorkOrders(r.data)).catch(() => {})
    }
    if (tab === 'invoices' && invoices.length === 0) {
      api.get<{ data: Invoice[] }>(`/invoices?client_id=${client.id}&limit=50`).then(r => setInvoices(r.data)).catch(() => {})
    }
    if (tab === 'floorplans' && floorPlans.length === 0) {
      api.get<FloorPlanItem[]>(`/clients/${client.id}/floor-plans`).then(setFloorPlans).catch(() => {})
    }
    if (tab === 'activity' && activityLog.length === 0) {
      api.get<{ data: ActivityEntry[] }>(`/activity-log?entity_type=client&entity_id=${client.id}&limit=50`).then(r => setActivityLog(r.data)).catch(() => {})
    }
  }, [tab, client, workOrders.length, invoices.length, floorPlans.length, activityLog.length])

  const handleSiteCreated = async () => {
    setShowAddSite(false)
    await loadData()
  }

  const openEditClient = () => {
    if (!client) return
    setEditForm({
      company_name: client.company_name || '',
      contact_name: client.contact_name || '',
      email: client.email || '',
      phone: client.phone || '',
      address_line1: client.address_line1 || '',
      address_line2: client.address_line2 || '',
      city: client.city || '',
      state: client.state || '',
      zip: client.zip || '',
      notes: client.notes || '',
    })
    setShowEditClient(true)
  }

  const handleSaveClient = async () => {
    if (!client) return
    setEditSaving(true)
    try {
      await api.patch(`/clients/${client.slug}`, editForm)
      setShowEditClient(false)
      await loadData()
    } catch { /* handled */ }
    finally { setEditSaving(false) }
  }

  if (loading) return <CardGridSkeleton cards={3} />
  if (!client) return <div className="p-6" style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Client not found.</div>

  const clientType = (client as Client & { client_type?: string }).client_type || 'client'

  return (
    <div className="page-enter">
      <Breadcrumb items={[{ label: 'Clients', to: '/clients' }, { label: client.company_name }]} />

      <EntityHeader
        icon={Building2Icon}
        title={client.company_name}
        badges={[
          { label: clientType === 'client' ? 'Active' : clientType.charAt(0).toUpperCase() + clientType.slice(1), variant: clientType === 'client' ? 'ok' as const : 'accent' as const, dot: clientType === 'client' },
        ]}
        metrics={[
          { icon: MapPinIcon, label: 'Sites', value: counts.sites },
          { icon: FileTextIcon, label: 'Proposals', value: counts.proposals },
          { icon: DollarIcon, label: '', value: `${fmtMoney(proposals.reduce((sum, p) => sum + (p.total || 0), 0))} Pipeline`, accent: true },
        ]}
        actions={[
          { label: 'Edit', onClick: openEditClient, variant: 'ghost' as const },
        ]}
      />

      <TabBar
        tabs={TAB_CONFIG.map(t => ({
          id: t.id,
          label: t.label,
          count: t.countKey ? counts[t.countKey as keyof Counts] : undefined,
        }))}
        active={tab}
        onChange={(id) => setTab(id as Tab)}
      />

      {/* Details Tab */}
      {tab === 'details' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlowCard style={{ padding: 'calc(20px * var(--density))' }} delay={100}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>Contact Info</h3>
            <dl className="space-y-2">
              {client.contact_name && <div className="flex justify-between"><dt style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Contact</dt><dd style={{ fontSize: 14, color: 'var(--text)' }}>{client.contact_name}</dd></div>}
              {client.email && <div className="flex justify-between"><dt style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Email</dt><dd><a href={`mailto:${client.email}`} className="hover:underline" style={{ fontSize: 14, color: 'var(--primary)' }}>{client.email}</a></dd></div>}
              {client.phone && <div className="flex justify-between"><dt style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Phone</dt><dd><a href={`tel:${client.phone}`} className="hover:underline mono" style={{ fontSize: 12, color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>{client.phone}</a></dd></div>}
            </dl>
          </GlowCard>
          <GlowCard style={{ padding: 'calc(20px * var(--density))' }} delay={180}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>Address</h3>
            <div style={{ fontSize: 14, color: 'var(--text)' }}>
              {client.address_line1 ? (
                (client as unknown as Record<string, unknown>).latitude && (client as unknown as Record<string, unknown>).longitude ? (
                  <a href={getGoogleMapsUrl((client as unknown as Record<string, unknown>).latitude as number, (client as unknown as Record<string, unknown>).longitude as number)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                    <p>{client.address_line1}</p>
                    {client.address_line2 && <p>{client.address_line2}</p>}
                    {(client.city || client.state || client.zip) && <p>{[client.city, client.state].filter(Boolean).join(', ')} {client.zip}</p>}
                  </a>
                ) : (
                  <>
                    <p>{client.address_line1}</p>
                    {client.address_line2 && <p>{client.address_line2}</p>}
                    {(client.city || client.state || client.zip) && <p>{[client.city, client.state].filter(Boolean).join(', ')} {client.zip}</p>}
                  </>
                )
              ) : (
                !client.city ? <p style={{ color: 'var(--text-tertiary)' }}>No address on file</p> : <p>{[client.city, client.state].filter(Boolean).join(', ')} {client.zip}</p>
              )}
            </div>
          </GlowCard>
          {client.notes && (
            <GlowCard className="md:col-span-2" style={{ padding: 'calc(20px * var(--density))' }} delay={260}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Notes</h3>
              <p className="whitespace-pre-wrap" style={{ fontSize: 14, color: 'var(--text)' }}>{client.notes}</p>
            </GlowCard>
          )}
          <GlowCard className="md:col-span-2" style={{ padding: 'calc(20px * var(--density))' }} delay={340}>
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)' }}>Locations</h3>
            </div>
            {sites.length > 0 ? (
              <div className="space-y-3">
                {sites.map(site => (
                  <Link key={site.id} to={`/sites/${site.slug}`} style={{
                    display: 'block', padding: 12, background: 'var(--bg-2)', borderRadius: 'var(--r-m)',
                    textDecoration: 'none', color: 'inherit',
                    transition: 'all var(--dur-f) var(--ease)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-3)'; e.currentTarget.style.transform = 'translateX(3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-2)'; e.currentTarget.style.transform = 'none'; }}>
                    <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{site.name}</span>
                      <div className="flex gap-2">
                        {site.is_primary === 1 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--primary-soft, rgba(59,130,246,0.1))', color: 'var(--primary)', fontWeight: 600 }}>Primary</span>}
                        {site.is_billing === 1 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--success-soft, rgba(5,150,105,0.1))', color: 'var(--success)', fontWeight: 600 }}>Billing</span>}
                      </div>
                    </div>
                    {(site as unknown as Record<string, unknown>).lat && (site as unknown as Record<string, unknown>).lng ? (
                      <a href={getGoogleMapsUrl((site as unknown as Record<string, unknown>).lat as number, (site as unknown as Record<string, unknown>).lng as number)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>
                        {[site.address, site.city, site.state, site.zip].filter(Boolean).join(', ') || 'No address'}
                      </a>
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{[site.address, site.city, site.state, site.zip].filter(Boolean).join(', ') || 'No address'}</div>
                    )}
                    {(site.site_contact_name || site.site_contact_phone) && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{site.site_contact_name}{site.site_contact_phone && <span className="mono" style={{ fontFamily: 'var(--font-mono)' }}> · {site.site_contact_phone}</span>}</div>}
                  </Link>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No locations yet.</p>
            )}
          </GlowCard>
        </div>
      )}

      {/* Proposals Tab */}
      {tab === 'proposals' && (
        <div>
          {proposals.length > 0 ? (
            <div className="space-y-3">
              {proposals.map(p => (
                <Link key={p.id} to={`/proposals/${p.slug}`} className="card flex items-center justify-between" style={{ padding: 16, textDecoration: 'none', color: 'inherit' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{p.title}</div>
                    <div className="mono" style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {p.imported_from && <span> · Imported from {p.imported_from}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={p.status} />
                    <span className="mono" style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{fmtMoney(p.total)}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyTab message="No proposals for this client yet." actionLabel="Create Proposal" actionLink={`/proposals?action=new&client=${slug}`} />
          )}
        </div>
      )}

      {/* Sites Tab */}
      {tab === 'sites' && (
        <div>
          <div className="flex justify-end" style={{ marginBottom: 12 }}>
            <Button onClick={() => setShowAddSite(true)} icon={PlusIcon} size="sm">Add Site</Button>
          </div>
          {sites.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sites.map(site => (
                <Link key={site.id} to={`/sites/${site.slug}`} className="card" style={{ padding: 16, textDecoration: 'none', color: 'inherit', transition: 'box-shadow 0.15s' }} onMouseEnter={e => (e.currentTarget.style.boxShadow = 'var(--shadow-md)')} onMouseLeave={e => (e.currentTarget.style.boxShadow = 'var(--shadow-xs)')}>
                  <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{site.name}</span>
                    <StatusBadge status={site.status} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{[site.address, site.city, site.state].filter(Boolean).join(', ') || 'No address'}</div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>Updated {new Date(site.updated_at).toLocaleDateString()}</div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyTab message="No sites for this client yet." actionLabel="+ Add Site" onClick={() => setShowAddSite(true)} />
          )}
        </div>
      )}

      {/* Work Orders Tab */}
      {tab === 'workorders' && (
        <div>
          <div className="flex justify-end" style={{ marginBottom: 12 }}>
            <Link to="/dispatch" className="v3-btn v3-btn-primary v3-btn-sm" style={{ textDecoration: 'none' }}>+ New Work Order</Link>
          </div>
          {workOrders.length > 0 ? (
            <GlowCard style={{ overflow: 'hidden', padding: 0 }} delay={100}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                    {['Title', 'Type', 'Priority', 'Status', 'Scheduled', 'Assigned To'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {workOrders.map(wo => (
                    <tr key={wo.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <Link to={`/dispatch/${wo.slug}`} style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }} className="hover:underline">{wo.title}</Link>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-secondary)' }}>{wo.type}</td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={wo.priority} /></td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={wo.status} /></td>
                      <td className="mono" style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-tertiary)' }}>{wo.scheduled_date ? new Date(wo.scheduled_date).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-secondary)' }}>{wo.assigned_user_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </GlowCard>
          ) : (
            <EmptyTab message="No work orders for this client yet." actionLabel="Create Work Order" actionLink="/dispatch" />
          )}
        </div>
      )}

      {/* Floor Plans Tab */}
      {tab === 'floorplans' && (
        <div>
          {floorPlans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {floorPlans.map(fp => (
                <Link key={fp.id} to={`/site-plans/${fp.slug}`} className="card" style={{ padding: 16, textDecoration: 'none', color: 'inherit' }}>
                  {fp.background_r2_key && (
                    <div style={{ height: 120, background: 'var(--bg-surface-hover)', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
                      <img src={`/api/files/${fp.background_r2_key}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )}
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{fp.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    {fp.site_name} · Floor {fp.floor_number}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyTab message="No floor plans found across this client's sites." />
          )}
        </div>
      )}

      {/* Invoices Tab */}
      {tab === 'invoices' && (
        <div>
          {invoices.length > 0 ? (
            <GlowCard style={{ overflow: 'hidden', padding: 0 }} delay={100}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                    {['Invoice #', 'Amount', 'Status', 'Issued', 'Due Date'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td className="mono" style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{inv.invoice_number}</td>
                      <td className="mono" style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{fmtMoney(inv.total)}</td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={inv.status} /></td>
                      <td className="mono" style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-tertiary)' }}>{inv.issued_date ? new Date(inv.issued_date).toLocaleDateString() : '—'}</td>
                      <td className="mono" style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-tertiary)' }}>{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </GlowCard>
          ) : (
            <EmptyTab message="No invoices for this client yet." />
          )}
        </div>
      )}

      {/* Activity Tab */}
      {tab === 'activity' && (
        <div>
          {activityLog.length > 0 ? (
            <div className="space-y-3">
              {activityLog.map(entry => (
                <div key={entry.id} className="card" style={{ padding: 12, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)', marginTop: 6, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--text)' }}>{entry.description || `${entry.action} ${entry.entity_type}`}</div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {entry.user_name && <span>{entry.user_name} · </span>}
                      {new Date(entry.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyTab message="No activity recorded yet." />
          )}
        </div>
      )}

      {/* Add Site Modal */}
      <SiteCreateModal
        isOpen={showAddSite}
        onClose={() => setShowAddSite(false)}
        onCreated={handleSiteCreated}
        clientId={client?.id}
      />

      {/* Edit Client Modal */}
      {showEditClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card" style={{ width: 520, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Edit Client</h2>
            <div className="space-y-3">
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Company Name *</label>
                <input value={editForm.company_name} onChange={e => setEditForm(f => ({ ...f, company_name: e.target.value }))} className="v3-input w-full" style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 }} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Contact Name</label>
                  <input value={editForm.contact_name} onChange={e => setEditForm(f => ({ ...f, contact_name: e.target.value }))} className="v3-input w-full" style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Phone</label>
                  <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className="v3-input w-full" style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Email</label>
                <input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} type="email" className="v3-input w-full" style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Address</label>
                <AddressAutocomplete
                  value={{ address_line1: editForm.address_line1, city: editForm.city, state: editForm.state, zip_code: editForm.zip }}
                  onChange={(addr) => setEditForm(f => ({ ...f, address_line1: addr.address_line1, city: addr.city, state: addr.state, zip: addr.zip_code }))}
                  showMapPreview={false}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Address Line 2</label>
                <input value={editForm.address_line2} onChange={e => setEditForm(f => ({ ...f, address_line2: e.target.value }))} className="v3-input w-full" style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 }} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>City</label>
                  <input value={editForm.city} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} className="v3-input w-full" style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>State</label>
                  <input value={editForm.state} onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))} className="v3-input w-full" style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Zip</label>
                  <input value={editForm.zip} onChange={e => setEditForm(f => ({ ...f, zip: e.target.value }))} className="v3-input w-full" style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8 }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Notes</label>
                <textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows={3} className="v3-textarea w-full" style={{ padding: '8px 12px', fontSize: 14, border: '1px solid var(--border-light)', borderRadius: 8, resize: 'vertical' }} />
              </div>
            </div>
            <div className="flex justify-end gap-2" style={{ marginTop: 20 }}>
              <Button variant="secondary" onClick={() => setShowEditClient(false)}>Cancel</Button>
              <Button onClick={handleSaveClient} loading={editSaving}>
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function EmptyTab({ message, actionLabel, actionLink, onClick }: { message: string; actionLabel?: string; actionLink?: string; onClick?: () => void }) {
  return (
    <div style={{
      textAlign: 'center', padding: '48px 24px',
      background: 'var(--bg-2)', borderRadius: 'var(--r-l)',
      border: '1px solid var(--border)',
    }}>
      <EmptyStateIcon size={40} style={{ color: 'var(--text-3)', margin: '0 auto 12px' }} />
      <p style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: actionLabel ? 16 : 0, lineHeight: 1.5 }}>{message}</p>
      {actionLabel && actionLink && (
        <Link to={actionLink} className="v3-btn v3-btn-primary" style={{ fontSize: 13, textDecoration: 'none' }}>{actionLabel}</Link>
      )}
      {actionLabel && onClick && (
        <Button onClick={onClick} size="sm">{actionLabel}</Button>
      )}
    </div>
  )
}
